import React from "react";
import { Box, Divider, Grid, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import TimeSlotV2Cst from "@components/TimeSlotV2Cst";
import { WizardStepProps } from "@components/WizardCst/types";
import { MediaType } from "src/interfaces/Media";
import ToggleSection from "@pages/Contents/V2/Corporate/Catalog/AddActivity/steps/MainInfoStep/sections/ToggleSection";
import AssociationFieldPlaceholder from "@pages/Contents/V2/Corporate/Catalog/AddActivity/steps/MainInfoStep/sections/AssociationFieldPlaceholder";
import {
  CompletionCriterion,
  CourseWizardData,
  PropaedeuticLogic,
} from "../types";
import { useLoTranslationProps } from "../../hooks/useLoTranslation";
import {
  COMPLETION_CRITERION_OPTIONS,
  PROPAEDEUTIC_LOGICS_OPTIONS,
} from "../config/courseOptions";

const SectionDivider: React.FC = () => <Divider sx={{ opacity: 0.6 }} />;

const SectionHeader: React.FC<{ title: string; description?: string }> = ({
  title,
  description,
}) => {
  const theme = useTheme();
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          sx={{
            fontSize: "13px",
            color: theme.palette.text.tertiary,
            mt: 0.5,
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

interface MainInfoStepProps extends WizardStepProps<CourseWizardData> {
  lockPublication?: boolean;
  lockPropaedeutic?: boolean;
  lockCompletableToggle?: boolean;
}

const MainInfoStep: React.FC<MainInfoStepProps> = ({
  data,
  setData,
  errors,
  lockPublication = false,
  lockPropaedeutic = false,
  lockCompletableToggle = false,
}) => {
  const { t } = useTranslation("wizard-course");
  const { t: tPicker } = useTranslation("media-library-picker");
  const { id: corporateId = "" } = useParams();

  const titleTrans = useLoTranslationProps("title", {
    header: t("fields.title"),
    maxValue: 70,
  });
  const descTrans = useLoTranslationProps("description", {
    header: t("fields.description"),
    maxValue: 256,
    multiline: true,
    rows: 3,
  });

  const errMsg = (key: string): string =>
    errors[key] ? t(`errors.${errors[key]}`) || "" : "";

  const toUtcMidnightISO = (input: Date | string | number): string => {
    const d = new Date(input);
    const utcMs = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
    return new Date(utcMs).toISOString();
  };

  const handleScheduleToggle = (next: boolean) => {
    if (next) {
      setData({ schedule_publication: true, publish_date: null });
    } else {
      setData({
        schedule_publication: false,
        publish_date: toUtcMidnightISO(new Date()),
      });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InputCst
            id="course-title"
            label={`${t("fields.title")} *`}
            value={data.title}
            onChange={(e: any) => setData({ title: e.target.value })}
            maxValue={70}
            error={!!errors.title}
            errorMessage={errMsg("title")}
            {...titleTrans}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputCst
            id="course-digited-code"
            label={t("fields.digited-code") || ""}
            value={data.digited_code}
            onChange={(e: any) => setData({ digited_code: e.target.value })}
            maxValue={30}
            error={!!errors.digited_code}
            errorMessage={errMsg("digited_code")}
          />
        </Grid>
      </Grid>

      <InputCst
        id="course-description"
        label={`${t("fields.description")} *`}
        value={data.description}
        onChange={(e: any) => setData({ description: e.target.value })}
        maxValue={256}
        multiline
        rows={3}
        error={!!errors.description}
        errorMessage={errMsg("description")}
        {...descTrans}
      />

      <SectionDivider />

      <ToggleSection
        question={t("sections.allow-additions.question")}
        description={t("sections.allow-additions.description") || undefined}
        checked={data.allow_post_publish_additions}
        onChange={(next) => setData({ allow_post_publish_additions: next })}
        noLabel={t("common.no") || "No"}
        yesLabel={t("common.yes") || "Sì"}
        disabled={lockCompletableToggle}
      />

      <SectionDivider />

      <ToggleSection
        question={t("sections.standalone.question")}
        description={t("sections.standalone.description") || undefined}
        checked={data.is_standalone}
        onChange={(next) => setData({ is_standalone: next })}
        noLabel={t("common.no") || "No"}
        yesLabel={t("common.yes") || "Sì"}
      />

      {data.is_standalone && (
        <>
          <SectionDivider />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <ToggleSection
              question={t("sections.publication.question")}
              description={t("sections.publication.description") || undefined}
              checked={data.schedule_publication}
              onChange={handleScheduleToggle}
              noLabel={t("common.no") || "No"}
              yesLabel={t("common.yes") || "Sì"}
              disabled={lockPublication}
            />

            {data.schedule_publication && (
              <DataPickerCst
                id="course-publish-date"
                label={`${t("fields.publish-date")} *`}
                value={data.publish_date ?? null}
                onChange={(value: any) =>
                  setData({
                    publish_date: value ? toUtcMidnightISO(value) : null,
                  })
                }
                error={!!errors.publish_date}
                errorMessage={errMsg("publish_date")}
                disabled={lockPublication}
              />
            )}
          </Box>
        </>
      )}

      <SectionDivider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          title={t("sections.propaedeutic.title")}
          description={t("sections.propaedeutic.description") || undefined}
        />
        <SelectCst
          id="course-propaedeutic-logics"
          label={`${t("fields.propaedeutic-logics")} *`}
          value={data.propaedeutic_logics || ""}
          onChange={(v: any) =>
            setData({ propaedeutic_logics: v as PropaedeuticLogic })
          }
          options={PROPAEDEUTIC_LOGICS_OPTIONS.map((o) => ({
            value: o.value,
            label: t(o.i18nKey) || o.value,
          }))}
          error={!!errors.propaedeutic_logics}
          errorMessage={errMsg("propaedeutic_logics")}
          disabled={lockPropaedeutic}
        />
      </Box>

      <SectionDivider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          title={t("sections.completion.title")}
          description={t("sections.completion.description") || undefined}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SelectCst
              id="course-completion-criterion"
              label={`${t("fields.completion-criterion")} *`}
              value={data.completion_criterion || ""}
              onChange={(v: any) =>
                setData({
                  completion_criterion: v as CompletionCriterion,
                })
              }
              options={COMPLETION_CRITERION_OPTIONS.map((o) => ({
                value: o.value,
                label: t(o.i18nKey) || o.value,
              }))}
              error={!!errors.completion_criterion}
              errorMessage={errMsg("completion_criterion")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputCst
              id="course-completion-value"
              label={`${t("fields.completion-value")} *`}
              value={data.completion_value}
              onChange={(e: any) =>
                setData({ completion_value: e.target.value })
              }
              type="number"
              min={0}
              error={!!errors.completion_value}
              errorMessage={errMsg("completion_value")}
            />
          </Grid>
        </Grid>
      </Box>

      <SectionDivider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          title={t("sections.cover.title")}
          description={t("sections.cover.description") || undefined}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <AssociationFieldPlaceholder
              label={t("fields.cover")}
              required
              value={data.cover_id}
              onChange={(next) => setData({ cover_id: next })}
              addLabel={t("common.add") || "Aggiungi"}
              emptyLabel={t("common.empty") || "Nessun elemento aggiunto"}
              error={!!errors.cover_id}
              errorMessage={errMsg("cover_id")}
              mediaType={MediaType.COVER}
              pickerTitle={tPicker("title-cover") || ""}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AssociationFieldPlaceholder
              label={t("fields.video-cover")}
              value={data.video_id}
              onChange={(next) => setData({ video_id: next })}
              addLabel={t("common.add") || "Aggiungi"}
              emptyLabel={t("common.empty") || "Nessun elemento aggiunto"}
              mediaType={MediaType.VIDEO}
              pickerTitle={tPicker("title-video") || ""}
            />
          </Grid>
        </Grid>
      </Box>

      {data.is_standalone && corporateId !== "1" && (
        <>
          <SectionDivider />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SectionHeader title={t("sections.limits.title")} />
            <TimeSlotV2Cst
              initialTimeSlots={data.time_slots}
              onChange={(slots) => setData({ time_slots: slots })}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default MainInfoStep;
